import {
    Column,
    ColumnDef,
    Row,
    SortDirection,
    SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getGroupedRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    useReactTable,
    GroupingState,
    ExpandedState,
    ColumnFiltersState,
    ColumnResizeDirection,
    ColumnResizeMode,
    VisibilityState,
    RowSelectionState,
    ColumnMeta,
} from "@tanstack/react-table";
import { Decimal } from "decimal.js";
import { HTMLAttributes, forwardRef, useState, useEffect } from "react";
import moment from "moment-timezone";
import { TableVirtuoso } from "react-virtuoso";
import { TableCell, TableHead, TableRow } from "./table";
import { cn } from "../../lib/utils";
import { safeParseDecimal } from "@/lib/api/common";
import { CommonDestinationTimezone } from "@/lib/utils";

// Original Table is wrapped with a <div> (see https://ui.shadcn.com/docs/components/table#radix-:r24:-content-manual),
// but here we don't want it, so let's use a new component with only <table> tag
const TableComponent = forwardRef<
    HTMLTableElement,
    React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
    <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
    />
));
TableComponent.displayName = "TableComponent";

const formatPercent = (num: number) => {
    return `${num.toFixed(1)}%`;
};

export interface KeyRow<TData> extends Row<TData> {
    key?: string;
}

interface TableRowProps<TData> {
    onCellClick?: (row: any, cell_id?: string) => void;
    onRowClick?: (row: any) => void;
    rows: KeyRow<TData>[];
    formId?: string
}

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    isCellClick?: boolean
  }
}

const TableRowComponent = <TData,>(data: TableRowProps<TData>) =>
    function getTableRow(props: HTMLAttributes<HTMLTableRowElement>) {
        // @ts-expect-error data-index is a valid attribute
        const index = props["data-index"];
        const row = data.rows[index];

        if (!row) return null;
        const isRowAggregate = row.getIsGrouped();

        return (
            <TableRow
                key={`${data.formId}:Row:${row.key || row.id}`}
                className={cn(data.onRowClick ? "cursor-pointer" : "")}
                data-state={row.getIsSelected() && "selected"}
                onClick={!!data.onRowClick ? () => (data as any).onRowClick(row?.original) : undefined}
                {...props}
            >
                {row.getVisibleCells().map((cell, rowIndex: number) => {
                    const isCellGrouped = cell.getIsGrouped();
                    const isCellAggregate = cell.getIsAggregated();
                    const isCellClick = (cell.column.columnDef as any).onCellClick || cell.column.columnDef.meta?.["isCellClick"];
                    return (
                        <TableCell
                            key={cell.id}
                            onClick={
                                isCellClick && !!data.onCellClick
                                    ? () =>
                                        data.onCellClick?.(
                                            row?.original,
                                            cell.column.columnDef.id,
                                        )
                                    : undefined
                            }
                            style={{ width: cell.column.getSize() }}
                            className={cn(
                                isCellClick && data.onCellClick ? "cursor-pointer" : "",
                            )}

                        >
                            {(isRowAggregate && !(isCellGrouped || isCellAggregate)) ? (flexRender(cell.column.columnDef.cell, cell.getContext())) : (null)}
                            {(isRowAggregate && (isCellGrouped)) && flexRender(cell.column.columnDef.cell, cell.getContext())}
                            {(isRowAggregate && (isCellAggregate)) ? (
                                flexRender(
                                    cell.column.columnDef.aggregatedCell || cell.column.columnDef.cell,
                                    cell.getContext()
                                )
                            ) : (null)}
                            {!isRowAggregate && (flexRender(cell.column.columnDef.cell, cell.getContext()))}

                        </TableCell>
                    );
                })}
            </TableRow>
        );
    };

function SortingIndicator({ isSorted }: { isSorted: SortDirection | false }) {
    if (!isSorted) return null;
    return (
        <div>
            {
                {
                    asc: "↑",
                    desc: "↓",
                }[isSorted]
            }
        </div>
    );
}

export interface DataTableProps<TData, TValue> {
    defaultGroupingOpen?: boolean;
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    height: string;
    onCellClick?: (row: any, cell_id?: string) => void;
    onRowClick?: (row: any) => void;
    className?: string;
    formId?: string;
    emptyResultsMessage?: string;
    defaultGrouping?: string[]
    useRowUuid?: boolean;
    defaultSorting?: SortingState
    enableColumnResizing?: boolean;
    columnVisibility?: VisibilityState;
    onColumnVisibilityChange?: (visibility: VisibilityState) => void;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: (selection: RowSelectionState) => void;
}

const renderEmptyPlaceholder = (columns: any, emptyResultsMessage?: string) => (props: any) => {
    return (
        <tbody>
            <tr>
                <td
                    colSpan={columns.length}
                    className="h-24 text-center"
                    style={{ textAlign: "center" }}
                >
                    {emptyResultsMessage || "No Results."}
                </td>
            </tr>
        </tbody>
    );
};

export function DataTable<TData, TValue>({
    onRowClick,
    onCellClick,
    columns,
    data,
    height,
    className,
    formId,
    emptyResultsMessage,
    defaultGrouping,
    defaultSorting,
    useRowUuid,
    enableColumnResizing,
    columnVisibility: externalColumnVisibility,
    onColumnVisibilityChange: externalOnColumnVisibilityChange,
    rowSelection: externalRowSelection,
    onRowSelectionChange: externalOnRowSelectionChange,
    defaultGroupingOpen
}: DataTableProps<TData, TValue>) {
    const [columnResizeMode, setColumnResizeMode] = useState<ColumnResizeMode>('onChange')
    const [columnResizeDirection, setColumnResizeDirection] = useState<ColumnResizeDirection>('ltr')
    const [grouping, setGrouping] = useState<GroupingState>(defaultGrouping || []);
    const [expanded, setExpanded] = useState<ExpandedState>(defaultGroupingOpen ? true : {});
    const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({})
    const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({})

    // Use external visibility state if provided, otherwise use internal state
    const columnVisibility = externalColumnVisibility ?? internalColumnVisibility;
    const rowSelection = externalRowSelection ?? internalRowSelection;

    const handleColumnVisibilityChange = (updaterOrValue: any) => {
        if (externalOnColumnVisibilityChange) {
            if (typeof updaterOrValue === 'function') {
                const newValue = updaterOrValue(columnVisibility);
                externalOnColumnVisibilityChange(newValue);
            } else {
                externalOnColumnVisibilityChange(updaterOrValue);
            }
        } else {
            setInternalColumnVisibility(updaterOrValue);
        }
    };

    const handleRowSelectionChange = (updaterOrValue: any) => {
        if (externalOnRowSelectionChange) {
            if (typeof updaterOrValue === 'function') {
                const newValue = updaterOrValue(rowSelection);
                externalOnRowSelectionChange(newValue);
            } else {
                externalOnRowSelectionChange(updaterOrValue);
            }
        } else {
            setInternalRowSelection(updaterOrValue);
        }
    };
    const [sorting, setSorting] = useState<SortingState>(defaultSorting || []);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
        []
    );
    const table = useReactTable({
        data,
        columns,
        state: {
            grouping,
            expanded,
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        initialState: {},
        sortingFns: {
            customerName: (rowA, rowB, col) => {
                const a_long_name: string = rowA.original?.Customer?.long_name?.toLocaleLowerCase() as any || "";
                const b_long_name: string = rowB.original?.Customer?.long_name?.toLocaleLowerCase() as any || "";
                return a_long_name.localeCompare(b_long_name);
            },
        },
        filterFns: {
            customerName: (row, columnId, filterValue) => {
                const long_name: string = row.original?.Customer?.long_name?.toLocaleLowerCase() as any || "";
                const filter_string: string = filterValue?.toLocaleLowerCase() as any;
                return long_name.includes(filter_string);
            },
            dateTimeFilter: (row, columnId, filterValue) => {
                const cellValue = row.getValue(columnId); // This is your Date object

                if (!cellValue) {
                    return false;
                }

                return [
                    moment.tz(cellValue, CommonDestinationTimezone).format("MM/DD/YYYY"),
                    moment.tz(cellValue, CommonDestinationTimezone).format("MM/D/YYYY"),
                    moment.tz(cellValue, CommonDestinationTimezone).format("MM-DD-YYYY"),
                    moment.tz(cellValue, CommonDestinationTimezone).format("MM-D-YYYY"),
                    moment.tz(cellValue, CommonDestinationTimezone).format("MMM DD YYYY"),
                    moment.tz(cellValue, CommonDestinationTimezone).format("MMM D YYYY"),
                ].some((formattedDate: string) => formattedDate.includes(String(filterValue)));
            },
            numberFilter: (row, columnId, filterValue) => {
                const rowValue = row.getValue(columnId);

                if (!filterValue) {
                    return true;
                }

                if (filterValue === '0') {
                    return String(rowValue) === '0';
                }

                return String(rowValue).includes(String(filterValue));
            },
            percentageFilter: (row, columnId, filterValue) => {
                const rowValue = row.getValue(columnId);

                if (rowValue === 0 && filterValue === "0") return true;
                if (rowValue === 0 && filterValue !== "0") return false;
                if (filterValue === '0') return false;
                if (typeof rowValue !== 'number') return false;


                if (!filterValue) {
                    return true;
                }

                const percent = formatPercent(rowValue || 0);
                return percent.includes(String(filterValue));
            }
        },
        onGroupingChange: setGrouping,
        onExpandedChange: setExpanded,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: handleColumnVisibilityChange,
        onRowSelectionChange: handleRowSelectionChange,
        getRowId: useRowUuid ? (row: any) => {
          return row.id;
        } : undefined,
        enableRowSelection: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        filterFromLeafRows: true, // search through the expanded rows
        maxLeafRowFilterDepth: 1,
        getSubRows: row => {
            return [];
        },
        aggregationFns: {
            decimalAggregate: (columnId, leafRows, childRows) => {
                const amount = childRows.reduce((acc, item) => {
                    return acc.plus(safeParseDecimal({})(item.original[columnId]).toNumber());
                }, new Decimal(0)).toNumber();

                return amount;
            },
        },
        enableGrouping: true,
        ...(enableColumnResizing ? ({
            columnResizeMode,
            columnResizeDirection,
        }) : ({})),
        autoResetExpanded: false,
    });

    const { rows } = table.getRowModel();

    return (
        <div className={cn("rounded-md border", className)}>
            <TableVirtuoso
                style={{ height }}
                totalCount={rows.length}
                components={{
                    Table: TableComponent,
                    TableRow: TableRowComponent({ formId, rows, onRowClick, onCellClick }),
                    EmptyPlaceholder: renderEmptyPlaceholder(columns, emptyResultsMessage),
                }}
                fixedHeaderContent={() =>
                    table.getHeaderGroups().map((headerGroup) => (
                        // Change header background color to non-transparent
                        <TableRow className="bg-card hover:bg-muted" key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead
                                        key={`${formId}:head:${header.id}`}
                                        colSpan={header.colSpan}
                                        style={{
                                            width: header.getSize(),
                                        }}
                                        className="relative"
                                    >
                                        {header.isPlaceholder ? null : (
                                            <div
                                                className="flex items-center"
                                                {...{
                                                    style: header.column.getCanSort()
                                                        ? {
                                                            cursor: "pointer",
                                                            userSelect: "none",
                                                        }
                                                        : {},
                                                    onClick: header.column.getToggleSortingHandler(),
                                                }}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )}
                                                <SortingIndicator
                                                    isSorted={header.column.getIsSorted()}
                                                />
                                                {enableColumnResizing && (
                                                    <div
                                                        {...{
                                                            onDoubleClick: () => header.column.resetSize(),
                                                            onMouseDown: header.getResizeHandler(),
                                                            onTouchStart: header.getResizeHandler(),
                                                            className: `resizer ${table.options.columnResizeDirection
                                                                } ${header.column.getIsResizing() ? 'isResizing' : ''
                                                                }`,
                                                            style: {
                                                                transform:
                                                                    columnResizeMode === 'onEnd' &&
                                                                        header.column.getIsResizing()
                                                                        ? `translateX(${(table.options.columnResizeDirection ===
                                                                            'rtl'
                                                                            ? -1
                                                                            : 1) *
                                                                        (table.getState().columnSizingInfo
                                                                            .deltaOffset ?? 0)
                                                                        }px)`
                                                                        : '',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {!header.isPlaceholder && header.column.getCanFilter() ? (
                                            <div>
                                                <Filter column={header.column} />
                                            </div>
                                        ) : null}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))
                }
            />
        </div>
    );
}

function Filter({ column }: { column: Column<any, unknown> }) {
    const columnFilterValue = column.getFilterValue()

    return (
        <DebouncedInput
            className="border shadow rounded w-full max-w-full"
            onChange={value => column.setFilterValue(value)}
            onClick={e => e.stopPropagation()}
            placeholder={`Search...`}
            type="text"
            value={(columnFilterValue ?? '') as string}
        />
    )
}

// A typical debounced input react component
function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = useState(initialValue)

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value])

    return (
        <input {...props} value={value} onChange={e => setValue(e.target.value)}
            style={{
                ...props.style,
                minWidth: 0,  // Allow shrinking
                boxSizing: 'border-box'
            }}
        />
    )
}
